import { createTheme, type Theme } from '@mui/material/styles'

// One theme for both apps. The member SPA and the admin SPA should feel
// like one intranet, which is half the point of merging ressu, webplan and
// fsm in the first place.
//
// Colours and type follow the Jukolan Pilotit brand pack v1.1: Pilot Blue
// as the light-mode primary, Night Navy for chrome, Deep Hangar as the
// dark page, and Beacon Amber as the single warm accent. Amber is a fill
// only; text on light uses Amber Text (#8A5300) to stay AA.

export const BRAND = {
  pilotBlue: '#003887',
  nightNavy: '#0f2440',
  deepHangar: '#0a1930',
  horizonSky: '#5a8fc0',
  skyBlue: '#8fb7e1',
  skyMist: '#e5eef6',
  beaconAmber: '#e0982f',
  amberText: '#8a5300',
  slate: '#3c4854',
  silver: '#a9afb7',
  cloud: '#f7f9fb',
} as const

// Saira Expanded Italic, uppercase: headlines, kickers, wordmark text.
export const DISPLAY_FONT = '"Saira Variable", "Saira", "Arial Narrow", sans-serif'
export const DISPLAY_STYLE = {
  fontFamily: DISPLAY_FONT,
  fontStyle: 'italic',
  fontWeight: 800,
  fontStretch: '125%',
  textTransform: 'uppercase',
  letterSpacing: '0.02em',
} as const

const PALETTES = {
  light: {
    primary: { main: BRAND.pilotBlue, light: '#3a62a6', dark: BRAND.nightNavy, contrastText: '#ffffff' },
    secondary: { main: BRAND.beaconAmber, light: '#f0b866', dark: BRAND.amberText, contrastText: BRAND.deepHangar },
    error: { main: '#c62828' },
    warning: { main: '#ed6c02' },
    success: { main: '#009e38' }, // the green huoltostatus.php used for hours remaining
    background: { default: BRAND.cloud, paper: '#ffffff' },
    text: { primary: BRAND.deepHangar, secondary: BRAND.slate },
    divider: 'rgba(15,36,64,0.12)',
  },
  dark: {
    primary: { main: BRAND.horizonSky, light: BRAND.skyBlue, dark: '#3d6d9c', contrastText: BRAND.deepHangar },
    secondary: { main: BRAND.beaconAmber, light: '#f0b866', dark: '#b8761a', contrastText: BRAND.deepHangar },
    error: { main: '#ef5350' },
    warning: { main: '#ffa726' },
    success: { main: '#4caf50' },
    background: { default: BRAND.deepHangar, paper: BRAND.nightNavy },
    text: { primary: BRAND.skyMist, secondary: '#a9b8cc' },
    divider: 'rgba(143,183,225,0.18)',
  },
} as const

export type ThemeMode = 'light' | 'dark'

export function createJupiTheme(mode: ThemeMode): Theme {
  return createTheme({
    palette: { mode, ...PALETTES[mode] },
    shape: { borderRadius: 10 },
    typography: {
      fontFamily: [
        '"Inter Variable"',
        '"Inter"',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        'sans-serif',
      ].join(','),
      h1: { fontWeight: 700 },
      h2: { fontWeight: 700 },
      h3: { fontWeight: 700 },
      h4: { fontWeight: 700 },
      h5: { fontWeight: 700 },
      h6: { fontWeight: 700 },
      button: { textTransform: 'none', fontWeight: 600 },
    },
    components: {
      // Night Navy chrome in both modes, with the brand's amber rule.
      MuiAppBar: {
        styleOverrides: {
          colorPrimary: {
            backgroundColor: BRAND.nightNavy,
            color: '#ffffff',
            backgroundImage: 'none',
            borderBottom: `3px solid ${BRAND.beaconAmber}`,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            boxShadow: mode === 'light' ? '0 10px 30px rgba(15,36,64,0.06)' : 'none',
            border: mode === 'light' ? '1px solid rgba(15,36,64,0.08)' : '1px solid rgba(143,183,225,0.14)',
            backgroundImage: 'none',
            overflow: 'hidden',
            position: 'relative',
          },
        },
      },
      // The member app is used one-handed on a phone at an airfield, often
      // in gloves. 44px is the smallest reliably tappable target.
      MuiButton: { styleOverrides: { root: { minHeight: 44 } } },
      MuiListItemButton: { styleOverrides: { root: { minHeight: 44 } } },
      MuiTextField: { defaultProps: { size: 'medium' } },
    },
  })
}

// Colours assigned to aircraft in the calendar. Chosen to stay
// distinguishable for the common forms of colour blindness -- a booking
// grid where two aircraft read as the same colour is worse than useless.
// Assigned by stable index so an aircraft keeps its colour between visits.
export const AIRCRAFT_COLORS = [
  '#4e79a7',
  '#59a14f',
  '#e15759',
  '#f28e2b',
  '#76b7b2',
  '#b07aa1',
  '#9c755f',
  '#edc948',
] as const

export function aircraftColor(index: number): string {
  return AIRCRAFT_COLORS[index % AIRCRAFT_COLORS.length] as string
}
